"use client"

import { useState, useEffect, useRef, useCallback, type CSSProperties } from "react"
import Link from "next/link"

// ─── Theme ────────────────────────────────────────────────────────────────────

interface Theme {
  bg: string; bg2: string; bg3: string;
  fg: string; fg2: string; fg3: string;
  accent: string; accentFg: string;
  border: string; borderStrong: string;
  cardBg: string; cardShadow: string;
  navBg: string; isDark: boolean;
}

const LIGHT: Theme = {
  bg: "#ffffff", bg2: "#f5f5f7", bg3: "#e8e8ed",
  fg: "#1d1d1f", fg2: "#6e6e73", fg3: "#aeaeb2",
  accent: "#0071e3", accentFg: "#ffffff",
  border: "rgba(0,0,0,0.08)", borderStrong: "rgba(0,0,0,0.16)",
  cardBg: "#ffffff", cardShadow: "0 2px 24px rgba(0,0,0,0.07), 0 1px 4px rgba(0,0,0,0.04)",
  navBg: "rgba(255,255,255,0.82)", isDark: false,
}
const DARK: Theme = {
  bg: "#000000", bg2: "#111113", bg3: "#1c1c1e",
  fg: "#f5f5f7", fg2: "#98989d", fg3: "#48484a",
  accent: "#2997ff", accentFg: "#ffffff",
  border: "rgba(255,255,255,0.09)", borderStrong: "rgba(255,255,255,0.18)",
  cardBg: "#1c1c1e", cardShadow: "0 2px 24px rgba(0,0,0,0.6), 0 1px 4px rgba(0,0,0,0.35)",
  navBg: "rgba(0,0,0,0.82)", isDark: true,
}

// ─── Data ─────────────────────────────────────────────────────────────────────

const LIFE_DIMS = [
  { accent: "#ff375f", label: "Magnetism",   tag: "Become the kind of person people orbit without knowing why — magnetic in every room, effortless in every conversation." },
  { accent: "#fbbf24", label: "Wealth",      tag: "Build the life where money is finally not the problem." },
  { accent: "#30d158", label: "Inner Peace", tag: "Quiet the noise. Move through life with unshakeable calm." },
  { accent: "#2997ff", label: "Influence",   tag: "Lead, persuade, inspire. Command every room that matters." },
  { accent: "#bf5af2", label: "Mastery",     tag: "Become dangerously good at the skills that define you — the kind of rare competence people can't ignore." },
  { accent: "#ff9500", label: "Vitality",    tag: "The energy, the body, the presence — aligned with your ambition." },
]

const FEATURE_SLIDES = [
  { word: "Clarity.",    sub: "See your entire life clearly — for the very first time.",           accent: "#2997ff", glow: "rgba(41,151,255,0.22)"  },
  { word: "Direction.",  sub: "Always know what deserves your energy, right now.",                 accent: "#30d158", glow: "rgba(48,209,88,0.18)"    },
  { word: "Growth.",     sub: "A measurably better version of you, week after week.",              accent: "#bf5af2", glow: "rgba(191,90,242,0.20)"   },
  { word: "Balance.",    sub: "Not perfection. The right focus, in exactly the right places.",     accent: "#ff9500", glow: "rgba(255,149,0,0.18)"    },
]

const PLATFORMS = [
  { label: "iPhone",       sub: "iOS 16+",      icon: PhoneIcon  },
  { label: "Apple Watch",  sub: "watchOS 9+",   icon: WatchIcon  },
  { label: "Mac & Web",    sub: "Any browser",  icon: LaptopIcon },
  { label: "Galaxy Watch", sub: "Wear OS 3+",   icon: WatchIcon  },
]

const OUTCOMES = [
  { icon: "◎", title: "Total awareness",   body: "Know exactly how every area of your life is performing — without any guesswork." },
  { icon: "◆", title: "Daily momentum",    body: "Each day delivers a small, precise action that compounds into extraordinary change." },
  { icon: "✦", title: "Real progress",     body: "Watch your life improve week by week. Streaks. Milestones. Measurable results." },
]

// ─── Icon components ──────────────────────────────────────────────────────────

function PhoneIcon({ size = 48, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="12" y="4" width="24" height="40" rx="5" stroke={color} strokeWidth="2.2"/>
      <rect x="17" y="7" width="14" height="26" rx="2" fill={color} fillOpacity="0.07"/>
      <circle cx="24" cy="38" r="2.2" fill={color} fillOpacity="0.5"/>
      <rect x="20" y="6" width="8" height="2" rx="1" fill={color} fillOpacity="0.35"/>
    </svg>
  )
}

function WatchIcon({ size = 48, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="10" y="11" width="28" height="26" rx="8" stroke={color} strokeWidth="2.2"/>
      <rect x="14" y="14" width="20" height="20" rx="5" fill={color} fillOpacity="0.07"/>
      <rect x="17" y="6"  width="14" height="6" rx="2" stroke={color} strokeWidth="1.5" opacity="0.4"/>
      <rect x="17" y="36" width="14" height="6" rx="2" stroke={color} strokeWidth="1.5" opacity="0.4"/>
      <circle cx="24" cy="24" r="5" stroke={color} strokeWidth="1.5" opacity="0.5"/>
      <line x1="24" y1="19" x2="24" y2="24" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
      <line x1="24" y1="24" x2="27" y2="26" stroke={color} strokeWidth="1.5" strokeLinecap="round"/>
    </svg>
  )
}

function LaptopIcon({ size = 48, color = "#fff" }: { size?: number; color?: string }) {
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" fill="none">
      <rect x="5"  y="9"  width="38" height="25" rx="3" stroke={color} strokeWidth="2.2"/>
      <rect x="9"  y="12" width="30" height="19" rx="1.5" fill={color} fillOpacity="0.07"/>
      <path d="M2 36h44" stroke={color} strokeWidth="2.2" strokeLinecap="round"/>
      <rect x="17" y="36" width="14" height="3" rx="1.5" fill={color} fillOpacity="0.35"/>
    </svg>
  )
}

// ─── Device mockups ───────────────────────────────────────────────────────────

function IPhoneMockup() {
  const colors = ["#4ade80","#60a5fa","#f472b6","#fbbf24","#34d399","#c084fc","#fb923c","#94a3b8"]
  return (
    <svg viewBox="0 0 230 470" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id="iphoneGlow" cx="50%" cy="44%" r="48%">
          <stop offset="0%"   stopColor="#7c3aed" stopOpacity="0.6"/>
          <stop offset="50%"  stopColor="#2563eb" stopOpacity="0.25"/>
          <stop offset="100%" stopColor="#0f172a" stopOpacity="0"/>
        </radialGradient>
        <radialGradient id="screenCenter" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#6d28d9" stopOpacity="0.55"/>
          <stop offset="70%"  stopColor="#1e1b4b" stopOpacity="0.2"/>
          <stop offset="100%" stopColor="#000"    stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="bodyGrad" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%"   stopColor="#3a3a42"/>
          <stop offset="100%" stopColor="#26262e"/>
        </linearGradient>
        <linearGradient id="bodyEdge" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#55555f"/>
          <stop offset="100%" stopColor="#33333b"/>
        </linearGradient>
        <clipPath id="screenClip">
          <rect x="10" y="10" width="210" height="450" rx="36"/>
        </clipPath>
      </defs>
      <rect x="0" y="0" width="230" height="470" rx="47" fill="url(#bodyEdge)"/>
      <rect x="1.5" y="1.5" width="227" height="467" rx="46" fill="url(#bodyGrad)"/>
      <rect x="0"   y="110" width="3" height="55" rx="1.5" fill="#48484f"/>
      <rect x="0"   y="175" width="3" height="55" rx="1.5" fill="#48484f"/>
      <rect x="227" y="140" width="3" height="80" rx="1.5" fill="#48484f"/>
      <rect x="10" y="10" width="210" height="450" rx="36" fill="#060610"/>
      <rect x="10" y="10" width="210" height="450" rx="36" fill="url(#iphoneGlow)"/>
      <g clipPath="url(#screenClip)">
        <rect x="10" y="10" width="210" height="50" fill="rgba(0,0,0,0.4)"/>
        <circle cx="115" cy="240" r="100" fill="url(#screenCenter)"/>
        <circle cx="115" cy="240" r="80" stroke="rgba(139,92,246,0.20)" strokeWidth="1" fill="none" strokeDasharray="6 5"/>
        <circle cx="115" cy="240" r="56" stroke="rgba(99,102,241,0.18)" strokeWidth="0.8" fill="none" strokeDasharray="4 6"/>
        {colors.map((color, i) => {
          const a = -Math.PI/2 + (2*Math.PI/8)*i
          return <circle key={i} cx={115 + 80*Math.cos(a)} cy={240 + 80*Math.sin(a)} r={4} fill={color} opacity={0.85}/>
        })}
        <polygon
          points={colors.map((_, i) => {
            const a = -Math.PI/2 + (2*Math.PI/8)*i
            return `${(115+80*Math.cos(a)).toFixed(1)},${(240+80*Math.sin(a)).toFixed(1)}`
          }).join(" ")}
          fill="rgba(139,92,246,0.10)" stroke="rgba(139,92,246,0.45)" strokeWidth="1.5"
        />
        <rect x="24" y="62"  width="140" height="30" rx="11" fill="rgba(124,58,237,0.30)" stroke="rgba(139,92,246,0.50)" strokeWidth="0.8"/>
        <rect x="24" y="97"  width="95"  height="20" rx="7"  fill="rgba(255,255,255,0.07)"/>
        <rect x="72" y="123" width="120" height="30" rx="11" fill="rgba(37,99,235,0.30)"  stroke="rgba(59,130,246,0.50)" strokeWidth="0.8"/>
        <circle cx="115" cy="240" r="20" fill="rgba(109,40,217,0.40)" stroke="rgba(139,92,246,0.70)" strokeWidth="1.2"/>
        <text x="115" y="246" textAnchor="middle" fill="white" fontSize="14" fontWeight="800" fontFamily="sans-serif">R</text>
        {[{y:368,w:140,c:"#4ade80"},{y:383,w:108,c:"#60a5fa"},{y:398,w:124,c:"#f472b6"},{y:413,w:90,c:"#fbbf24"}].map((bar,i)=>(
          <g key={i}>
            <rect x="24" y={bar.y} width="182" height="9" rx="4.5" fill="rgba(255,255,255,0.07)"/>
            <rect x="24" y={bar.y} width={bar.w} height="9" rx="4.5" fill={bar.c} opacity={0.85}/>
          </g>
        ))}
      </g>
      <rect x="76" y="19" width="78" height="27" rx="13.5" fill="#0a0a10"/>
      <rect x="88" y="452" width="54" height="4" rx="2" fill="rgba(255,255,255,0.25)"/>
      <rect x="10" y="10" width="210" height="450" rx="36" fill="none" stroke="rgba(255,255,255,0.10)" strokeWidth="1"/>
    </svg>
  )
}

function AppleWatchMockup() {
  return (
    <svg viewBox="0 0 160 186" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id="watchGlow" cx="50%" cy="50%" r="50%">
          <stop offset="0%"   stopColor="#5ac8fa" stopOpacity="0.45"/>
          <stop offset="100%" stopColor="#5ac8fa" stopOpacity="0"/>
        </radialGradient>
        <clipPath id="watchScreen">
          <rect x="10" y="10" width="140" height="166" rx="38"/>
        </clipPath>
      </defs>
      <rect x="32" y="0"   width="96" height="20" rx="6" fill="#1e1e22"/>
      <rect x="32" y="166" width="96" height="20" rx="6" fill="#1e1e22"/>
      <rect x="1"  y="9"   width="158" height="168" rx="42" fill="#1e1e22" stroke="#333338" strokeWidth="1"/>
      <rect x="152" y="60" width="8" height="30" rx="4" fill="#2c2c30" stroke="#3a3a3e" strokeWidth="0.8"/>
      <rect x="10" y="10" width="140" height="166" rx="38" fill="#040408"/>
      <rect x="10" y="10" width="140" height="166" rx="38" fill="url(#watchGlow)"/>
      <g clipPath="url(#watchScreen)">
        <circle cx="80" cy="88" r="50" stroke="rgba(255,255,255,0.06)" strokeWidth="8" fill="none"/>
        <circle cx="80" cy="88" r="50" stroke="#ff375f" strokeWidth="8" fill="none" strokeDasharray="220 314" strokeLinecap="round" transform="rotate(-90 80 88)"/>
        <circle cx="80" cy="88" r="38" stroke="rgba(255,255,255,0.06)" strokeWidth="7" fill="none"/>
        <circle cx="80" cy="88" r="38" stroke="#30d158" strokeWidth="7" fill="none" strokeDasharray="170 239" strokeLinecap="round" transform="rotate(-90 80 88)"/>
        <circle cx="80" cy="88" r="26" stroke="rgba(255,255,255,0.06)" strokeWidth="6" fill="none"/>
        <circle cx="80" cy="88" r="26" stroke="#5ac8fa" strokeWidth="6" fill="none" strokeDasharray="110 163" strokeLinecap="round" transform="rotate(-90 80 88)"/>
        <text x="80" y="94"  textAnchor="middle" fill="white" fontSize="16" fontWeight="700" fontFamily="sans-serif" opacity="0.75">R</text>
        <text x="80" y="36"  textAnchor="middle" fill="rgba(255,255,255,0.5)" fontSize="13" fontWeight="500" fontFamily="sans-serif">9:41</text>
        <text x="80" y="155" textAnchor="middle" fill="rgba(255,255,255,0.3)" fontSize="9" fontWeight="600" fontFamily="sans-serif" letterSpacing="0.08em">REBALANCER</text>
      </g>
      <rect x="10" y="10" width="140" height="166" rx="38" fill="none" stroke="rgba(255,255,255,0.08)" strokeWidth="1"/>
    </svg>
  )
}

function MacBookMockup() {
  return (
    <svg viewBox="0 0 380 240" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ width: "100%", height: "100%" }}>
      <defs>
        <radialGradient id="mbGlow" cx="50%" cy="40%" r="55%">
          <stop offset="0%"   stopColor="#7c3aed" stopOpacity="0.50"/>
          <stop offset="60%"  stopColor="#2563eb" stopOpacity="0.18"/>
          <stop offset="100%" stopColor="#000"    stopOpacity="0"/>
        </radialGradient>
        <linearGradient id="mbBody" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#3a3a42"/>
          <stop offset="100%" stopColor="#26262e"/>
        </linearGradient>
        <linearGradient id="mbBase" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#2e2e36"/>
          <stop offset="100%" stopColor="#1e1e24"/>
        </linearGradient>
        <clipPath id="mbScreen">
          <rect x="22" y="8" width="336" height="200" rx="6"/>
        </clipPath>
      </defs>
      <rect x="14" y="4" width="352" height="210" rx="10" fill="url(#mbBody)" stroke="#48484f" strokeWidth="1"/>
      <rect x="22" y="8" width="336" height="200" rx="6" fill="#060610"/>
      <rect x="22" y="8" width="336" height="200" rx="6" fill="url(#mbGlow)"/>
      <g clipPath="url(#mbScreen)">
        <rect x="22" y="8"  width="336" height="20" fill="rgba(0,0,0,0.55)"/>
        <circle cx="38"  cy="18" r="4.5" fill="#ff5f57"/>
        <circle cx="54"  cy="18" r="4.5" fill="#febc2e"/>
        <circle cx="70"  cy="18" r="4.5" fill="#28c840"/>
        <rect x="22" y="28" width="80" height="180" fill="rgba(255,255,255,0.035)"/>
        {[0,1,2,3,4,5].map(i => (
          <rect key={i} x="30" y={42+i*24} width={i===1?58:44} height="8" rx="4" fill={i===1?"rgba(139,92,246,0.50)":"rgba(255,255,255,0.10)"}/>
        ))}
        <circle cx="240" cy="115" r="80" fill="rgba(109,40,217,0.18)"/>
        {[70,52,34].map((r,i)=>(
          <circle key={i} cx="240" cy="115" r={r} stroke="rgba(139,92,246,0.25)" strokeWidth="0.8" fill="none" strokeDasharray="5 5"/>
        ))}
        {[0,1,2,3,4,5,6,7].map(i=>{
          const a=-Math.PI/2+(2*Math.PI/8)*i
          const cols=["#4ade80","#60a5fa","#f472b6","#fbbf24","#34d399","#c084fc","#fb923c","#94a3b8"]
          return <circle key={i} cx={240+70*Math.cos(a)} cy={115+70*Math.sin(a)} r={3.5} fill={cols[i]} opacity={0.9}/>
        })}
        <polygon
          points={[0,1,2,3,4,5,6,7].map(i=>{
            const a=-Math.PI/2+(2*Math.PI/8)*i
            return `${(240+70*Math.cos(a)).toFixed(1)},${(115+70*Math.sin(a)).toFixed(1)}`
          }).join(" ")}
          fill="rgba(139,92,246,0.10)" stroke="rgba(139,92,246,0.45)" strokeWidth="1.2"
        />
        <circle cx="240" cy="115" r="18" fill="rgba(109,40,217,0.40)" stroke="rgba(139,92,246,0.70)" strokeWidth="1.2"/>
        <text x="240" y="120" textAnchor="middle" fill="white" fontSize="12" fontWeight="800" fontFamily="sans-serif">R</text>
        <rect x="102" y="38" width="120" height="22" rx="8" fill="rgba(124,58,237,0.30)" stroke="rgba(139,92,246,0.45)" strokeWidth="0.8"/>
        <rect x="102" y="68" width="80"  height="14" rx="5" fill="rgba(255,255,255,0.07)"/>
        <rect x="122" y="88" width="110" height="22" rx="8" fill="rgba(37,99,235,0.30)"  stroke="rgba(59,130,246,0.45)" strokeWidth="0.8"/>
        {[{y:168,w:100,c:"#4ade80"},{y:180,w:76,c:"#60a5fa"},{y:192,w:88,c:"#f472b6"}].map((b,i)=>(
          <g key={i}>
            <rect x="102" y={b.y} width="130" height="6" rx="3" fill="rgba(255,255,255,0.07)"/>
            <rect x="102" y={b.y} width={b.w}  height="6" rx="3" fill={b.c} opacity={0.85}/>
          </g>
        ))}
      </g>
      <circle cx="190" cy="16" r="2.5" fill="#1a1a24"/>
      <rect x="22" y="8" width="336" height="200" rx="6" fill="none" stroke="rgba(255,255,255,0.09)" strokeWidth="1"/>
      <rect x="0"   y="214" width="380" height="8"  rx="2" fill="url(#mbBase)"/>
      <rect x="60"  y="220" width="260" height="14" rx="2" fill="#1e1e24"/>
      <rect x="148" y="222" width="84"  height="4"  rx="2" fill="#2a2a32"/>
      <rect x="0"   y="228" width="380" height="2"  rx="1" fill="rgba(0,0,0,0.4)"/>
    </svg>
  )
}

// ─── Reveal hook ──────────────────────────────────────────────────────────────

function useReveal(threshold = 0.12) {
  const ref = useRef<HTMLDivElement>(null)
  const [visible, setVisible] = useState(false)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const obs = new IntersectionObserver(
      ([e]) => e.isIntersecting && setVisible(true),
      { threshold }
    )
    obs.observe(el)
    return () => obs.disconnect()
  }, [threshold])
  return [ref, visible] as const
}

// ─── Outcome card ─────────────────────────────────────────────────────────────

function OutcomeCard({ item, t, delayClass }: { item: typeof OUTCOMES[0]; t: Theme; delayClass: string }) {
  const [ref, visible] = useReveal()
  const [hovered, setHovered] = useState(false)
  return (
    <div
      ref={ref}
      className={`reveal ${delayClass} ${visible ? "visible" : ""}`}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        padding: "36px 32px", borderRadius: 22,
        backgroundColor: hovered ? (t.isDark ? "#252528" : "#fff") : t.bg2,
        border: `1px solid ${hovered ? t.borderStrong : t.border}`,
        transform: hovered ? "translateY(-5px)" : "translateY(0)",
        boxShadow: hovered ? t.cardShadow : "none",
        transition: "all 0.28s ease",
      }}
    >
      <div style={{ fontSize: 30, marginBottom: 20, color: t.accent }}>{item.icon}</div>
      <h3 style={{ fontSize: 22, fontWeight: 700, letterSpacing: "-0.025em", color: t.fg, margin: "0 0 12px" }}>{item.title}</h3>
      <p style={{ fontSize: 15, color: t.fg2, lineHeight: 1.65, margin: 0, letterSpacing: "-0.01em" }}>{item.body}</p>
    </div>
  )
}

// ─── Platform card ────────────────────────────────────────────────────────────

function PlatformCard({ label, sub, IconComp, t, delayClass }: { label: string; sub: string; IconComp: React.FC<{size?: number; color?: string}>; t: Theme; delayClass: string }) {
  const [ref, visible] = useReveal()
  const [hovered, setHovered] = useState(false)
  return (
    <div ref={ref} className={`reveal ${delayClass} ${visible ? "visible" : ""}`}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: "32px 20px", borderRadius: 20, textAlign: "center", backgroundColor: hovered ? (t.isDark ? "#252528" : "#fff") : t.bg2, border: `1px solid ${hovered ? t.borderStrong : t.border}`, transform: hovered ? "translateY(-4px)" : "translateY(0)", boxShadow: hovered ? t.cardShadow : "none", transition: "all 0.25s ease" }}>
      <div style={{ marginBottom: 16, opacity: hovered ? 1 : 0.65, transition: "opacity 0.25s ease", display: "flex", justifyContent: "center", alignItems: "center" }}>
        <IconComp size={44} color={hovered ? t.accent : t.fg2} />
      </div>
      <div style={{ fontSize: 15, fontWeight: 600, color: t.fg, letterSpacing: "-0.02em", marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 12, color: t.fg3, fontWeight: 500 }}>{sub}</div>
    </div>
  )
}

// ─── Life Dimension illustrations ─────────────────────────────────────────────

function MagnetismIll({ a }: { a: string }) {
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <circle cx="100" cy="65" r="55" fill={a} fillOpacity="0.07"/>
      <circle cx="100" cy="65" r="40" stroke={a} strokeOpacity="0.13" strokeWidth="0.8" fill="none"/>
      <circle cx="36"  cy="44" r="11" fill={a} opacity="0.78"/>
      <rect x="28" y="57" width="16" height="26" rx="8" fill={a} opacity="0.58"/>
      <circle cx="164" cy="44" r="11" fill={a} opacity="0.78"/>
      <rect x="156" y="57" width="16" height="26" rx="8" fill={a} opacity="0.58"/>
      <line x1="54" y1="53" x2="83"  y2="61"  stroke={a} strokeWidth="1.6" strokeDasharray="4 4" opacity="0.55"/>
      <line x1="146" y1="53" x2="117" y2="61" stroke={a} strokeWidth="1.6" strokeDasharray="4 4" opacity="0.55"/>
      <circle cx="100" cy="63" r="17" fill={a} fillOpacity="0.13"/>
      <path d="M100 57 C100 54 97 51.5 94 53 C91 54.5 91 58.5 93 61.5 L100 69 L107 61.5 C109 58.5 109 54.5 106 53 C103 51.5 100 54 100 57Z" fill={a}/>
    </svg>
  )
}

function WealthIll({ a }: { a: string }) {
  const base = 112
  const bars = [{x:22,h:24},{x:52,h:42},{x:82,h:60},{x:112,h:78},{x:142,h:94}]
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <ellipse cx="130" cy="100" rx="68" ry="34" fill={a} fillOpacity="0.08"/>
      {bars.map((b,i)=><rect key={i} x={b.x} y={base-b.h} width="22" height={b.h} rx="5" fill={a} opacity={0.33+i*0.13}/>)}
      <path d={`M33 ${base-24} L63 ${base-42} L93 ${base-60} L123 ${base-78} L153 ${base-94}`} stroke={a} strokeWidth="2" fill="none" opacity="0.65" strokeLinecap="round" strokeLinejoin="round"/>
      <circle cx="153" cy="54" r="10" stroke={a} strokeWidth="1.8" fill={a} fillOpacity="0.15" opacity="0.7"/>
      <text x="153" y="58" textAnchor="middle" fill={a} fontSize="10" fontWeight="800" fontFamily="sans-serif" opacity="0.85">$</text>
    </svg>
  )
}

function PeaceIll({ a }: { a: string }) {
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <circle cx="100" cy="70" r="56" fill={a} fillOpacity="0.07"/>
      <circle cx="100" cy="70" r="50" stroke={a} strokeOpacity="0.10" strokeWidth="0.8" fill="none"/>
      <circle cx="100" cy="70" r="36" stroke={a} strokeOpacity="0.16" strokeWidth="0.9" fill="none"/>
      <circle cx="100" cy="22" r="9"  fill={a} opacity="0.60"/>
      <circle cx="105" cy="19" r="7"  fill="#0d0d16" opacity="0.92"/>
      <circle cx="100" cy="52" r="11" fill={a} opacity="0.82"/>
      <path d="M87 65 Q100 62 113 65 L117 82 Q100 86 83 82 Z" fill={a} opacity="0.63"/>
    </svg>
  )
}

function InfluenceIll({ a }: { a: string }) {
  const outer=[{cx:42,cy:26},{cx:162,cy:26},{cx:26,cy:88},{cx:178,cy:88},{cx:76,cy:114},{cx:130,cy:114}]
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <circle cx="100" cy="62" r="54" fill={a} fillOpacity="0.07"/>
      {outer.map((n,i)=><line key={i} x1="100" y1="62" x2={n.cx} y2={n.cy} stroke={a} strokeWidth="1.2" opacity="0.30" strokeDasharray="4 5"/>)}
      {outer.map((n,i)=><circle key={i} cx={n.cx} cy={n.cy} r="6.5" fill={a} opacity="0.50"/>)}
      <circle cx="100" cy="62" r="18" fill={a} opacity="0.90"/>
      <circle cx="100" cy="57" r="5"  fill="white" fillOpacity="0.76"/>
      <path d="M93 68 Q100 65 107 68" stroke="white" strokeWidth="2.2" fill="none" opacity="0.66" strokeLinecap="round"/>
    </svg>
  )
}

function MasteryIll({ a }: { a: string }) {
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <ellipse cx="100" cy="78" rx="66" ry="46" fill={a} fillOpacity="0.08"/>
      <path d="M100 20 L70 65 L130 65 Z" fill={a} opacity="0.57"/>
      <path d="M70 65 L100 114 L130 65 Z" fill={a} opacity="0.84"/>
      <path d="M100 5 L102 14 L111 12 L103 17 L111 22 L102 20 L100 29 L98 20 L89 22 L97 17 L89 12 L98 14 Z" fill={a} opacity="0.90"/>
    </svg>
  )
}

function VitalityIll({ a }: { a: string }) {
  return (
    <svg viewBox="0 0 200 130" fill="none" style={{ width: "100%", height: "100%" }}>
      <ellipse cx="115" cy="72" rx="72" ry="46" fill={a} fillOpacity="0.08"/>
      <line x1="6"  y1="48" x2="62" y2="48" stroke={a} strokeWidth="3"   opacity="0.62" strokeLinecap="round"/>
      <line x1="14" y1="63" x2="64" y2="63" stroke={a} strokeWidth="2.2" opacity="0.42" strokeLinecap="round"/>
      <circle cx="124" cy="30" r="12" fill={a} opacity="0.88"/>
      <line x1="124" y1="42" x2="120" y2="70" stroke={a} strokeWidth="10" strokeLinecap="round" opacity="0.78"/>
      <line x1="122" y1="55" x2="102" y2="43" stroke={a} strokeWidth="8"  strokeLinecap="round" opacity="0.72"/>
      <path d="M172 14 L160 46 L170 46 L158 78 L194 36 L181 36 Z" fill={a} opacity="0.74"/>
    </svg>
  )
}

type IllComp = (props: { a: string }) => React.ReactElement
const DIM_ILLS: Record<string, IllComp> = {
  "Magnetism":   MagnetismIll,
  "Wealth":      WealthIll,
  "Inner Peace": PeaceIll,
  "Influence":   InfluenceIll,
  "Mastery":     MasteryIll,
  "Vitality":    VitalityIll,
}

function ImageDimCard({ dim, style: extraStyle }: { dim: typeof LIFE_DIMS[0]; style?: CSSProperties }) {
  const [hovered, setHovered] = useState(false)
  const Ill = DIM_ILLS[dim.label]
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: 20, overflow: "hidden", cursor: "default",
        background: "linear-gradient(160deg, #0d0d16 0%, #111020 100%)",
        border: `1px solid ${hovered ? dim.accent + "42" : "rgba(255,255,255,0.07)"}`,
        transform: hovered ? "translateY(-5px)" : "translateY(0)",
        transition: "transform 0.4s cubic-bezier(0.4,0,0.2,1), box-shadow 0.4s ease, border-color 0.3s",
        boxShadow: hovered ? `0 24px 56px rgba(0,0,0,0.40), 0 0 0 1px ${dim.accent}26` : "0 4px 16px rgba(0,0,0,0.20)",
        display: "flex", flexDirection: "column" as const, ...extraStyle,
      }}
    >
      <div style={{ flex: 1, minHeight: 0, overflow: "hidden", padding: "22px 18px 10px", display: "flex", alignItems: "center", justifyContent: "center" }}>
        {Ill && <Ill a={dim.accent} />}
      </div>
      <div style={{ flexShrink: 0, padding: "14px 22px 20px", borderTop: "1px solid rgba(255,255,255,0.05)" }}>
        <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: "0.13em", color: dim.accent, marginBottom: 5, textTransform: "uppercase" as const }}>{dim.label}</div>
        <p style={{ fontSize: 13, color: "rgba(255,255,255,0.60)", fontWeight: 400, lineHeight: 1.52, margin: 0, letterSpacing: "-0.01em" }}>{dim.tag}</p>
      </div>
    </div>
  )
}

// ─── Nav ──────────────────────────────────────────────────────────────────────

const NAV_LINKS = [
  { label: "Experience", href: "#experience" },
  { label: "Platforms",  href: "#platforms"  },
  { label: "About",      href: "#about"       },
]

function Nav({ t, onToggle }: { t: Theme; onToggle: () => void }) {
  const [scrolled, setScrolled] = useState(false)
  useEffect(() => {
    const fn = () => setScrolled(window.scrollY > 60)
    window.addEventListener("scroll", fn, { passive: true })
    return () => window.removeEventListener("scroll", fn)
  }, [])

  const linkColor      = scrolled ? t.fg2 : (t.isDark ? "rgba(255,255,255,0.70)" : t.fg2)
  const linkHoverColor = scrolled ? t.fg  : (t.isDark ? "#ffffff" : t.fg)

  return (
    <nav style={{
      position: "fixed", top: 0, left: 0, right: 0, zIndex: 100,
      display: "flex", alignItems: "center", justifyContent: "space-between",
      padding: "0 clamp(16px, 4vw, 48px)", height: 54,
      backgroundColor: scrolled ? t.navBg : "transparent",
      backdropFilter: scrolled ? "blur(24px) saturate(180%)" : "none",
      WebkitBackdropFilter: scrolled ? "blur(24px) saturate(180%)" : "none",
      borderBottom: `1px solid ${scrolled ? t.border : "transparent"}`,
      transition: "background-color 0.35s ease, border-color 0.35s ease",
    }}>
      <a href="#" style={{ textDecoration: "none", display: "flex", alignItems: "center", gap: 9, transition: "opacity 0.2s" }}
        onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = "0.8")}
        onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.opacity = "1")}>
        <span style={{
          display: "inline-flex", width: 28, height: 28, borderRadius: "50%",
          background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)",
          alignItems: "center", justifyContent: "center",
          fontSize: 12, color: "#fff", fontWeight: 800,
          boxShadow: "0 2px 10px rgba(109,40,217,0.50)",
        }}>R</span>
        <span style={{ fontSize: 16, fontWeight: 700, letterSpacing: "-0.025em", color: scrolled ? t.fg : (t.isDark ? "#fff" : t.fg), transition: "color 0.35s ease" }}>
          Rebalancer
        </span>
      </a>

      <div className="hidden sm:flex" style={{ gap: 36, alignItems: "center" }}>
        {NAV_LINKS.map(({ label, href }) => (
          <a key={label} href={href}
            style={{ fontSize: 13.5, fontWeight: 400, color: linkColor, textDecoration: "none", letterSpacing: "-0.01em", transition: "color 0.18s ease" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = linkHoverColor)}
            onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = linkColor)}
          >{label}</a>
        ))}
      </div>

      <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
        <button onClick={onToggle} aria-label="Toggle dark mode"
          style={{
            display: "flex", alignItems: "center", gap: 6,
            padding: "5px 10px", borderRadius: 20,
            border: `1px solid ${scrolled ? t.border : (t.isDark ? "rgba(255,255,255,0.18)" : t.border)}`,
            backgroundColor: scrolled ? t.bg2 : (t.isDark ? "rgba(255,255,255,0.08)" : t.bg2),
            cursor: "pointer", transition: "all 0.25s ease",
          }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={t.isDark ? "rgba(255,255,255,0.35)" : "#fbbf24"} strokeWidth="2.2" strokeLinecap="round">
            <circle cx="12" cy="12" r="4"/><line x1="12" y1="2" x2="12" y2="4"/><line x1="12" y1="20" x2="12" y2="22"/>
            <line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/>
            <line x1="2" y1="12" x2="4" y2="12"/><line x1="20" y1="12" x2="22" y2="12"/>
            <line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
          </svg>
          <div style={{ width: 28, height: 16, borderRadius: 8, backgroundColor: t.isDark ? t.accent : t.bg3, position: "relative", transition: "background-color 0.25s" }}>
            <div style={{ position: "absolute", top: 2, left: t.isDark ? 13 : 2, width: 12, height: 12, borderRadius: "50%", backgroundColor: "#fff", transition: "left 0.25s cubic-bezier(0.4,0,0.2,1)", boxShadow: "0 1px 3px rgba(0,0,0,0.3)" }} />
          </div>
          <svg width="12" height="12" viewBox="0 0 24 24" fill={t.isDark ? "rgba(255,255,255,0.8)" : "rgba(0,0,0,0.25)"} stroke="none">
            <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
          </svg>
        </button>

        <Link href="/register"
          style={{
            fontSize: 13, fontWeight: 600, color: "#fff",
            background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)",
            textDecoration: "none", padding: "8px 18px", borderRadius: 20,
            letterSpacing: "-0.01em", whiteSpace: "nowrap",
            transition: "opacity 0.2s, transform 0.2s",
            boxShadow: "0 4px 14px rgba(109,40,217,0.4)",
            display: "inline-block",
          }}
          onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.85"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
          onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}
        >Get Started Free</Link>
      </div>
    </nav>
  )
}

// ─── Hero ─────────────────────────────────────────────────────────────────────

function HeroSection({ t }: { t: Theme }) {
  return (
    <section style={{
      minHeight: "100vh", backgroundColor: t.bg,
      display: "flex", alignItems: "center",
      padding: "clamp(60px, 10vw, 80px) clamp(16px, 5vw, 60px) 60px", position: "relative", overflow: "hidden",
    }}>
      <div style={{ position: "absolute", top: "50%", right: "5%", transform: "translateY(-50%)", width: 640, height: 640, background: t.isDark ? "radial-gradient(ellipse, rgba(109,40,217,0.20) 0%, rgba(37,99,235,0.10) 40%, transparent 68%)" : "radial-gradient(ellipse, rgba(109,40,217,0.09) 0%, rgba(37,99,235,0.05) 40%, transparent 68%)", pointerEvents: "none", filter: "blur(70px)" }} />
      <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, height: 200, background: t.isDark ? "linear-gradient(to top, rgba(0,0,0,0.6), transparent)" : "linear-gradient(to top, rgba(255,255,255,0.9), transparent)", pointerEvents: "none" }} />

      <div className="flex flex-col lg:flex-row items-center" style={{ position: "relative", zIndex: 1, maxWidth: 1200, margin: "0 auto", width: "100%", gap: 48 }}>
        <div style={{ flex: "0 0 auto", maxWidth: 530, width: "100%" }}>
          <div className="word-0" style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 11, fontWeight: 700, color: "#30d158", letterSpacing: "0.12em", marginBottom: 24, padding: "5px 14px", borderRadius: 20, border: "1px solid rgba(48,209,88,0.30)", backgroundColor: "rgba(48,209,88,0.08)" }}>
            <span style={{ width: 5, height: 5, borderRadius: "50%", backgroundColor: "#30d158", display: "inline-block", boxShadow: "0 0 6px #30d158" }} />
            THE #1 LIFE OPERATING SYSTEM
          </div>

          <h1 style={{ fontSize: "clamp(50px, 6vw, 84px)", fontWeight: 800, letterSpacing: "-0.045em", lineHeight: 0.95, margin: "0 0 20px", color: t.fg }}>
            <span className="word-1" style={{ display: "block" }}>Your most</span>
            <span className="word-2 gradient-text" style={{ display: "block", backgroundImage: "linear-gradient(135deg, #2997ff 0%, #5ac8fa 50%, #30d158 100%)" }}>
              balanced life.
            </span>
          </h1>

          <p className="word-3" style={{ fontSize: "clamp(15px, 1.5vw, 17px)", fontWeight: 400, color: t.fg2, lineHeight: 1.65, margin: "0 0 32px", letterSpacing: "-0.01em", maxWidth: 400 }}>
            One intelligent app for every dimension of your life —{" "}
            <span style={{ color: t.fg, fontWeight: 500 }}>guided, tracked, and transformed.</span>
          </p>

          <div className="word-4" style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
              <Link href="/register"
                style={{ display: "inline-flex", alignItems: "center", gap: 10, fontSize: 15, fontWeight: 600, color: "#fff", background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)", textDecoration: "none", padding: "14px 28px", borderRadius: 980, letterSpacing: "-0.01em", transition: "opacity 0.2s, transform 0.2s", whiteSpace: "nowrap", boxShadow: "0 8px 32px rgba(109,40,217,0.45)" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
                ✦ Get Started Free
                <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
              </Link>
              <a href="#experience"
                style={{ display: "inline-flex", alignItems: "center", gap: 8, fontSize: 14, fontWeight: 500, color: t.fg2, textDecoration: "none", padding: "14px 20px", borderRadius: 980, border: `1px solid ${t.border}`, backgroundColor: t.bg2, transition: "all 0.2s", whiteSpace: "nowrap" }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = t.fg; (e.currentTarget as HTMLAnchorElement).style.borderColor = t.borderStrong; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = t.fg2; (e.currentTarget as HTMLAnchorElement).style.borderColor = t.border; }}>
                <svg width="14" height="14" viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="7" stroke="currentColor" strokeWidth="1.5" fill="none"/><path d="M6.5 5.5l4 2.5-4 2.5V5.5z" fill="currentColor"/></svg>
                See it in action
              </a>
            </div>

            <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
              {["Free to start", "All devices", "No complexity"].map((item) => (
                <div key={item} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 500, color: t.fg3, whiteSpace: "nowrap" }}>
                  <svg width="13" height="13" viewBox="0 0 13 13" fill="none"><circle cx="6.5" cy="6.5" r="6" stroke="rgba(48,209,88,0.6)" strokeWidth="1"/><path d="M4 6.5l2 2 3-3.5" stroke="#30d158" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {item}
                </div>
              ))}
            </div>

            <div style={{ display: "flex", alignItems: "center", gap: 14, paddingTop: 4 }}>
              <div style={{ display: "flex" }}>
                {["#ff375f","#0a84ff","#30d158","#bf5af2","#ff9500"].map((color, i) => (
                  <div key={i} style={{ width: 30, height: 30, borderRadius: "50%", background: `radial-gradient(circle at 35% 35%, ${color}cc, ${color}66)`, border: `2px solid ${t.bg}`, marginLeft: i > 0 ? -9 : 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, color: "#fff" }}>
                    {["A","B","C","D","E"][i]}
                  </div>
                ))}
              </div>
              <div>
                <div style={{ display: "flex", gap: 2, marginBottom: 2 }}>
                  {[1,2,3,4,5].map(i => <span key={i} style={{ color: "#fbbf24", fontSize: 12 }}>★</span>)}
                  <span style={{ color: t.fg2, fontSize: 12, marginLeft: 4, fontWeight: 600 }}>4.9</span>
                </div>
                <div style={{ fontSize: 11, color: t.fg3, fontWeight: 500 }}>Loved by 10,000+ seekers</div>
              </div>
            </div>
          </div>
        </div>

        <div className="word-5 hidden lg:block" style={{ flex: 1, position: "relative", height: 520, minWidth: 320 }}>
          <div style={{ position: "absolute", top: "50%", left: "42%", transform: "translate(-50%,-50%)", width: 480, height: 480, background: t.isDark ? "radial-gradient(ellipse, rgba(109,40,217,0.32) 0%, rgba(37,99,235,0.14) 40%, transparent 68%)" : "radial-gradient(ellipse, rgba(109,40,217,0.12) 0%, rgba(37,99,235,0.06) 40%, transparent 68%)", filter: "blur(56px)", pointerEvents: "none", zIndex: 0 }} />
          <div style={{ position: "absolute", left: "18%", top: "2%", transform: "rotate(-5deg)", width: 240, zIndex: 2, animation: "floatPhone 6s ease-in-out infinite", filter: t.isDark ? "drop-shadow(0 40px 80px rgba(0,0,0,0.95)) drop-shadow(0 0 40px rgba(109,40,217,0.35))" : "drop-shadow(0 32px 60px rgba(0,0,0,0.22)) drop-shadow(0 0 32px rgba(109,40,217,0.18))" }}>
            <IPhoneMockup />
          </div>
          <div style={{ position: "absolute", right: "10%", top: "10%", width: 148, zIndex: 3, animation: "floatWatch 5.5s ease-in-out infinite", filter: t.isDark ? "drop-shadow(0 24px 48px rgba(0,0,0,0.9)) drop-shadow(0 0 24px rgba(90,200,250,0.30))" : "drop-shadow(0 20px 40px rgba(0,0,0,0.18)) drop-shadow(0 0 20px rgba(90,200,250,0.14))" }}>
            <AppleWatchMockup />
          </div>
        </div>
      </div>

      <div style={{ position: "absolute", bottom: 28, left: "50%", transform: "translateX(-50%)", display: "flex", flexDirection: "column", alignItems: "center", gap: 5, opacity: 0.3 }}>
        <span style={{ fontSize: 10, letterSpacing: "0.14em", color: t.fg, fontWeight: 600 }}>SCROLL</span>
        <svg width="14" height="22" viewBox="0 0 16 24" fill="none"><rect x="6" y="3" width="4" height="7" rx="2" fill={t.isDark ? "white" : "#1d1d1f"} opacity="0.6"/><rect x="0.5" y="0.5" width="15" height="23" rx="7.5" stroke={t.isDark ? "white" : "#1d1d1f"} strokeWidth="1"/></svg>
      </div>
    </section>
  )
}

// ─── Statement ────────────────────────────────────────────────────────────────

function StatementSection({ t }: { t: Theme }) {
  const [ref, visible] = useReveal(0.2)
  return (
    <section style={{ backgroundColor: t.bg, padding: "clamp(60px, 10vw, 120px) clamp(16px, 5vw, 24px)", textAlign: "center" }}>
      <div ref={ref} className={`reveal ${visible ? "visible" : ""}`} style={{ maxWidth: 800, margin: "0 auto" }}>
        <h2 style={{ fontSize: "clamp(36px, 5vw, 68px)", fontWeight: 700, letterSpacing: "-0.04em", lineHeight: 1.05, color: t.fg, margin: "0 0 24px" }}>
          One app.<br /><span style={{ color: t.fg3 }}>Your entire life.</span>
        </h2>
        <p style={{ fontSize: "clamp(17px, 2vw, 21px)", color: t.fg2, lineHeight: 1.6, letterSpacing: "-0.01em", maxWidth: 560, margin: "0 auto" }}>
          Rebalancer quietly understands every aspect of who you are — and guides you, daily, toward the life you have always imagined.
        </p>
      </div>
    </section>
  )
}

// ─── Feature slides ───────────────────────────────────────────────────────────

function FeatureSlider({ t }: { t: Theme }) {
  const [current, setCurrent] = useState(0)
  const [animKey, setAnimKey]  = useState(0)

  useEffect(() => {
    const tid = setInterval(() => {
      setCurrent((c) => (c + 1) % FEATURE_SLIDES.length)
      setAnimKey((k) => k + 1)
    }, 3800)
    return () => clearInterval(tid)
  }, [])

  const goTo = (i: number) => { setCurrent(i); setAnimKey((k) => k + 1) }
  const slide = FEATURE_SLIDES[current]

  return (
    <section id="experience" style={{ minHeight: "80vh", backgroundColor: t.bg2, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "clamp(60px, 8vw, 100px) clamp(16px, 5vw, 24px)", textAlign: "center", position: "relative", overflow: "hidden", borderTop: `1px solid ${t.border}` }}>
      <div style={{ position: "absolute", inset: 0, background: `radial-gradient(ellipse at 50% 60%, ${slide.glow} 0%, transparent 65%)`, transition: "background 1s ease", pointerEvents: "none" }} />
      <div style={{ position: "relative", zIndex: 1 }}>
        <p style={{ fontSize: 11, fontWeight: 700, letterSpacing: "0.18em", color: t.fg3, marginBottom: 48 }}>WHAT YOU&apos;LL EXPERIENCE</p>
        <div key={`word-${animKey}`} style={{ animation: "slideFadeIn 0.55s cubic-bezier(0.4,0,0.2,1) both" }}>
          <h2 style={{ fontSize: "clamp(72px, 14vw, 160px)", fontWeight: 800, letterSpacing: "-0.05em", lineHeight: 0.9, color: slide.accent, margin: "0 0 32px", textShadow: t.isDark ? `0 0 80px ${slide.glow}` : "none" }}>
            {slide.word}
          </h2>
          <p style={{ fontSize: "clamp(17px, 2.2vw, 22px)", color: t.fg2, fontWeight: 400, letterSpacing: "-0.01em", lineHeight: 1.5, maxWidth: 500, margin: "0 auto" }}>
            {slide.sub}
          </p>
        </div>
        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 64 }}>
          {FEATURE_SLIDES.map((_, i) => (
            <button key={i} onClick={() => goTo(i)}
              style={{ width: i === current ? 28 : 8, height: 8, borderRadius: 4, backgroundColor: i === current ? slide.accent : "rgba(255,255,255,0.2)", border: "none", cursor: "pointer", padding: 0, transition: "all 0.4s cubic-bezier(0.4,0,0.2,1)" }} />
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Outcomes ─────────────────────────────────────────────────────────────────

function OutcomesSection({ t }: { t: Theme }) {
  const [hRef, hVisible] = useReveal()
  return (
    <section style={{ backgroundColor: t.bg2, padding: "clamp(60px, 8vw, 100px) clamp(16px, 5vw, 24px)", borderTop: `1px solid ${t.border}` }}>
      <div style={{ maxWidth: 1040, margin: "0 auto" }}>
        <div ref={hRef} className={`reveal ${hVisible ? "visible" : ""}`} style={{ textAlign: "center", marginBottom: 64 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: t.accent, letterSpacing: "0.1em", marginBottom: 16 }}>THE REBALANCER DIFFERENCE</p>
          <h2 style={{ fontSize: "clamp(36px, 4.5vw, 56px)", fontWeight: 700, letterSpacing: "-0.035em", lineHeight: 1.05, color: t.fg, margin: 0 }}>
            What changes when<br />everything is in focus.
          </h2>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
          {OUTCOMES.map((item, i) => (
            <OutcomeCard key={item.title} item={item} t={t} delayClass={`reveal-delay-${i + 1}`} />
          ))}
        </div>
      </div>
    </section>
  )
}

// ─── Agent section ────────────────────────────────────────────────────────────

const AGENT_FEATURES = [
  { accent: "#2997ff", icon: "📲", title: "It reaches out to you",   body: "Your AI doesn't wait. It sends check-ins, nudges, and calls when your momentum is slipping — before you even notice." },
  { accent: "#30d158", icon: "📈", title: "Tracks every dimension",  body: "Progress across all areas of your life — in real time. No journal required. Just live, and your guide keeps score." },
  { accent: "#bf5af2", icon: "🎯", title: "Adapts your plan daily",  body: "Life changes fast. Your AI adjusts your focus every morning based on what you actually need — not what you planned last week." },
  { accent: "#ff9500", icon: "🏆", title: "Won't let you give up",   body: "When you're about to quit, your Rebalancer steps in. Hard truths, real encouragement, and a path back — every single time." },
]

function AgentFeatureCard({ f, t, delayClass }: { f: typeof AGENT_FEATURES[0]; t: Theme; delayClass: string }) {
  const [ref, visible] = useReveal()
  const [hovered, setHovered] = useState(false)
  return (
    <div ref={ref} className={`reveal ${delayClass} ${visible ? "visible" : ""}`}
      onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: "28px 24px", borderRadius: 18, backgroundColor: hovered ? (t.isDark ? "#252528" : "#fff") : t.bg2, border: `1px solid ${hovered ? f.accent + "55" : t.border}`, boxShadow: hovered ? `0 20px 48px ${f.accent}18, ${t.cardShadow}` : "none", transform: hovered ? "translateY(-4px)" : "translateY(0)", transition: "all 0.28s ease" }}>
      <div style={{ fontSize: 26, marginBottom: 14 }}>{f.icon}</div>
      <h4 style={{ fontSize: 15, fontWeight: 700, color: t.fg, margin: "0 0 8px", letterSpacing: "-0.02em" }}>{f.title}</h4>
      <p style={{ fontSize: 13, color: t.fg2, lineHeight: 1.65, margin: 0 }}>{f.body}</p>
    </div>
  )
}

function AgentSection({ t }: { t: Theme }) {
  const [hRef, hVisible] = useReveal()
  return (
    <section style={{ backgroundColor: t.bg, padding: "clamp(60px, 10vw, 120px) clamp(16px, 5vw, 24px)", borderTop: `1px solid ${t.border}` }}>
      <div style={{ maxWidth: 1080, margin: "0 auto" }}>
        <div ref={hRef} className={`reveal ${hVisible ? "visible" : ""}`} style={{ marginBottom: 56 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: "#ff375f", letterSpacing: "0.1em", marginBottom: 16 }}>YOUR LIFE OS</p>
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 40, flexWrap: "wrap" }}>
            <h2 style={{ fontSize: "clamp(34px, 4vw, 58px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.05, color: t.fg, margin: 0 }}>
              An OS that fights for<br />your best life — daily.
            </h2>
            <p style={{ fontSize: 16, color: t.fg2, lineHeight: 1.7, letterSpacing: "-0.01em", maxWidth: 400, margin: 0, flexShrink: 0 }}>
              Most apps wait for you to show up.<br />Rebalancer comes to you.
            </p>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" style={{ marginBottom: 20 }}>
          {AGENT_FEATURES.map((f, i) => (
            <AgentFeatureCard key={f.title} f={f} t={t} delayClass={`reveal-delay-${i + 1}`} />
          ))}
        </div>
        {/* Proof strip */}
        <div className="reveal reveal-delay-3" style={{ borderRadius: 24, background: "linear-gradient(135deg, #0f0f1a 0%, #1a0a2e 55%, #0a1628 100%)", padding: "clamp(24px, 4vw, 36px) clamp(20px, 4vw, 44px)", position: "relative", overflow: "hidden" }}>
          <div style={{ position: "absolute", top: "-30%", left: "30%", width: 500, height: 300, background: "radial-gradient(ellipse, rgba(109,40,217,0.30) 0%, transparent 70%)", filter: "blur(60px)", pointerEvents: "none" }} />
          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6" style={{ position: "relative", zIndex: 1 }}>
            <div style={{ flex: "0 0 auto", maxWidth: 280 }}>
              <div style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 9, fontWeight: 700, color: "#30d158", letterSpacing: "0.14em", marginBottom: 12, padding: "3px 10px", borderRadius: 20, border: "1px solid rgba(48,209,88,0.25)", backgroundColor: "rgba(48,209,88,0.08)" }}>
                <span style={{ width: 4, height: 4, borderRadius: "50%", backgroundColor: "#30d158", boxShadow: "0 0 5px #30d158", display: "inline-block" }} />
                ALWAYS ON
              </div>
              <h3 style={{ fontSize: "clamp(18px, 2vw, 22px)", fontWeight: 800, color: "#f5f5f7", letterSpacing: "-0.03em", lineHeight: 1.25, margin: 0 }}>
                Your Life OS never sleeps, never judges, never quits.
              </h3>
            </div>
            <div className="flex flex-1 w-full sm:w-auto">
              {[{ num: "10,000+", label: "lives running on Rebalancer" }, { num: "4.9 ★", label: "avg rating, all platforms" }].map((s, i) => (
                <div key={s.num} style={{ flex: 1, textAlign: "center" as const, padding: "0 16px", borderRight: i < 1 ? "1px solid rgba(255,255,255,0.08)" : "none" }}>
                  <div style={{ fontSize: "clamp(24px, 2.8vw, 34px)", fontWeight: 800, color: "#f5f5f7", letterSpacing: "-0.04em", lineHeight: 1, marginBottom: 5 }}>{s.num}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.30)", lineHeight: 1.4 }}>{s.label}</div>
                </div>
              ))}
            </div>
            <div style={{ flexShrink: 0 }}>
              <Link href="/register" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "13px 26px", borderRadius: 980, background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)", color: "#fff", fontSize: 14, fontWeight: 600, textDecoration: "none", letterSpacing: "-0.01em", boxShadow: "0 6px 24px rgba(109,40,217,0.48)", whiteSpace: "nowrap" as const }}
                onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; }}>
                Start winning your life →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Life Dimensions ──────────────────────────────────────────────────────────

function LifeDimsSection({ t }: { t: Theme }) {
  const [hRef, hVisible] = useReveal()
  return (
    <section style={{ backgroundColor: t.bg2, padding: "clamp(60px, 9vw, 110px) clamp(16px, 5vw, 24px)", borderTop: `1px solid ${t.border}` }}>
      <div style={{ maxWidth: 1120, margin: "0 auto" }}>
        <div ref={hRef} className={`reveal ${hVisible ? "visible" : ""}`} style={{ textAlign: "center", marginBottom: 64 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: t.accent, letterSpacing: "0.1em", marginBottom: 16 }}>WHAT COULD CHANGE FOR YOU</p>
          <h2 style={{ fontSize: "clamp(36px, 4.5vw, 58px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.05, color: t.fg, margin: "0 0 20px" }}>
            Whatever you&apos;re after —<br />you&apos;ll find it here.
          </h2>
          <p style={{ fontSize: 17, color: t.fg2, maxWidth: 500, margin: "0 auto", lineHeight: 1.65, letterSpacing: "-0.01em" }}>
            One honest conversation. A life you actually designed — not one that just happened to you.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" style={{ gap: 14 }}>
          <div className="lg:col-span-2"><ImageDimCard dim={LIFE_DIMS[0]} style={{ height: "100%", minHeight: 220 }} /></div>
          <ImageDimCard dim={LIFE_DIMS[1]} style={{ height: "100%", minHeight: 220 }} />
          <ImageDimCard dim={LIFE_DIMS[2]} style={{ height: "100%", minHeight: 220 }} />
          <ImageDimCard dim={LIFE_DIMS[3]} style={{ height: "100%", minHeight: 220 }} />
          <div className="lg:col-span-2"><ImageDimCard dim={LIFE_DIMS[4]} style={{ height: "100%", minHeight: 220 }} /></div>
          <ImageDimCard dim={LIFE_DIMS[5]} style={{ height: "100%", minHeight: 220 }} />
        </div>
        <p style={{ textAlign: "center", marginTop: 40, fontSize: 14, color: t.fg3, letterSpacing: "-0.01em" }}>
          Every dimension. One guide. <span style={{ color: t.accent, fontWeight: 600 }}>Your Rebalancer.</span>
        </p>
      </div>
    </section>
  )
}

// ─── Platforms ────────────────────────────────────────────────────────────────

function PlatformsSection({ t }: { t: Theme }) {
  const [ref, visible] = useReveal()
  return (
    <section id="platforms" style={{ backgroundColor: t.bg, padding: "clamp(60px, 8vw, 100px) clamp(16px, 5vw, 24px)", borderTop: `1px solid ${t.border}` }}>
      <div style={{ maxWidth: 860, margin: "0 auto" }}>
        <div ref={ref} className={`reveal ${visible ? "visible" : ""}`} style={{ textAlign: "center", marginBottom: 72 }}>
          <p style={{ fontSize: 12, fontWeight: 700, color: t.accent, letterSpacing: "0.1em", marginBottom: 16 }}>AVAILABLE EVERYWHERE</p>
          <h2 style={{ fontSize: "clamp(36px, 4.5vw, 56px)", fontWeight: 700, letterSpacing: "-0.035em", lineHeight: 1.05, color: t.fg, margin: 0 }}>
            Every device.<br />One life.
          </h2>
          <p style={{ marginTop: 20, fontSize: 17, color: t.fg2, maxWidth: 480, margin: "20px auto 0", lineHeight: 1.6, letterSpacing: "-0.01em" }}>
            Rebalancer moves with you — on your wrist, in your pocket, at your desk.
          </p>
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {PLATFORMS.map((p, i) => (
            <PlatformCard key={p.label} label={p.label} sub={p.sub} IconComp={p.icon} t={t} delayClass={`reveal-delay-${i + 1}`} />
          ))}
        </div>
        <div className="flex flex-wrap justify-center items-end" style={{ marginTop: 56, gap: "clamp(16px, 4vw, 48px)" }}>
          <div style={{ width: "clamp(72px, 15vw, 108px)", aspectRatio: "108/220", animation: "floatPhone 6s ease-in-out infinite", filter: t.isDark ? "drop-shadow(0 20px 40px rgba(0,0,0,0.8))" : "drop-shadow(0 20px 40px rgba(0,0,0,0.2))" }}>
            <IPhoneMockup />
          </div>
          <div className="hidden sm:block" style={{ width: "clamp(100px, 20vw, 189px)", aspectRatio: "160/186", animation: "floatWatch 5.5s ease-in-out infinite", filter: t.isDark ? "drop-shadow(0 16px 32px rgba(0,0,0,0.7))" : "drop-shadow(0 16px 32px rgba(0,0,0,0.15))" }}>
            <AppleWatchMockup />
          </div>
          <div className="hidden md:block" style={{ width: "clamp(200px, 35vw, 348px)", aspectRatio: "380/240", animation: "floatAndroid 7s ease-in-out infinite", filter: t.isDark ? "drop-shadow(0 20px 40px rgba(0,0,0,0.8))" : "drop-shadow(0 20px 40px rgba(0,0,0,0.2))" }}>
            <MacBookMockup />
          </div>
        </div>
      </div>
    </section>
  )
}

// ─── Philosophy ───────────────────────────────────────────────────────────────

function PhilosophySection({ t }: { t: Theme }) {
  const [ref, visible] = useReveal(0.2)
  return (
    <section id="about" style={{ backgroundColor: t.bg2, padding: "clamp(60px, 10vw, 130px) clamp(16px, 5vw, 24px)", borderTop: `1px solid ${t.border}` }}>
      <div ref={ref} className={`reveal ${visible ? "visible" : ""}`} style={{ maxWidth: 740, margin: "0 auto", textAlign: "center" }}>
        <div style={{ fontSize: 88, color: t.isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.05)", lineHeight: 0.55, marginBottom: 40, fontWeight: 800 }}>&ldquo;</div>
        <blockquote style={{ fontSize: "clamp(22px, 3vw, 34px)", fontWeight: 500, color: t.fg, lineHeight: 1.38, letterSpacing: "-0.025em", margin: 0 }}>
          Most people spend their lives reacting. Rebalancer gives you the clarity to act with intention — in every single dimension of your life.
        </blockquote>
        <div style={{ marginTop: 40, width: 40, height: 2, backgroundColor: t.accent, borderRadius: 2, margin: "40px auto 0", opacity: 0.6 }} />
        <div style={{ marginTop: 20, fontSize: 12, color: t.fg3, letterSpacing: "0.1em", fontWeight: 600 }}>THE REBALANCER PHILOSOPHY</div>
      </div>
    </section>
  )
}

// ─── Download / CTA ───────────────────────────────────────────────────────────

function DownloadSection({ t }: { t: Theme }) {
  const [ref, visible] = useReveal(0.15)
  return (
    <section id="download" style={{ backgroundColor: t.bg2, padding: "clamp(60px, 10vw, 130px) clamp(16px, 5vw, 24px)", textAlign: "center", position: "relative", overflow: "hidden", borderTop: `1px solid ${t.border}` }}>
      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%,-50%)", width: 600, height: 400, background: `radial-gradient(ellipse, ${t.isDark ? "rgba(41,151,255,0.12)" : "rgba(41,151,255,0.06)"} 0%, transparent 70%)`, filter: "blur(40px)", pointerEvents: "none" }} />
      <div ref={ref} className={`reveal ${visible ? "visible" : ""}`} style={{ position: "relative", zIndex: 1, maxWidth: 640, margin: "0 auto" }}>
        <p style={{ fontSize: 12, fontWeight: 700, color: t.fg3, letterSpacing: "0.1em", marginBottom: 24 }}>GET STARTED FREE</p>
        <h2 style={{ fontSize: "clamp(40px, 5.5vw, 72px)", fontWeight: 800, letterSpacing: "-0.04em", lineHeight: 1.0, color: t.fg, margin: "0 0 24px" }}>
          Your balanced life<br />starts today.
        </h2>
        <p style={{ fontSize: 17, color: t.fg2, lineHeight: 1.6, letterSpacing: "-0.01em", marginBottom: 52 }}>
          Sign up for Rebalancer. Have one honest conversation.<br />Everything changes from there.
        </p>
        <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
          <Link href="/register" style={{ display: "inline-flex", alignItems: "center", gap: 10, padding: "16px 32px", borderRadius: 980, background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)", color: "#fff", fontSize: 16, fontWeight: 600, textDecoration: "none", letterSpacing: "-0.01em", boxShadow: "0 8px 32px rgba(109,40,217,0.45)" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "0.88"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(-1px)"; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.opacity = "1"; (e.currentTarget as HTMLAnchorElement).style.transform = "translateY(0)"; }}>
            ✦ Create Your Account
            <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><path d="M3 8h10M9 4l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Link>
          <Link href="/login" style={{ display: "inline-flex", alignItems: "center", gap: 8, padding: "16px 28px", borderRadius: 980, border: `1px solid ${t.border}`, color: t.fg2, fontSize: 15, fontWeight: 500, textDecoration: "none", letterSpacing: "-0.01em", transition: "all 0.2s" }}
            onMouseEnter={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = t.fg; (e.currentTarget as HTMLAnchorElement).style.borderColor = t.borderStrong; }}
            onMouseLeave={(e) => { (e.currentTarget as HTMLAnchorElement).style.color = t.fg2; (e.currentTarget as HTMLAnchorElement).style.borderColor = t.border; }}>
            Sign In →
          </Link>
        </div>
        <p style={{ marginTop: 28, fontSize: 12, color: t.fg3, letterSpacing: "0.04em" }}>
          Free to start · Web · No complexity
        </p>
      </div>
    </section>
  )
}

// ─── Footer ───────────────────────────────────────────────────────────────────

function Footer({ t }: { t: Theme }) {
  return (
    <footer style={{ backgroundColor: t.bg2, borderTop: `1px solid ${t.border}`, padding: "32px clamp(16px, 5vw, 44px)", display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 16 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: t.fg, letterSpacing: "-0.01em", display: "flex", alignItems: "center", gap: 8 }}>
        <span style={{ display: "inline-flex", width: 22, height: 22, borderRadius: "50%", background: "linear-gradient(135deg, #6d28d9 0%, #2563eb 100%)", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#fff", fontWeight: 800 }}>R</span>
        Rebalancer
      </div>
      <div style={{ display: "flex", gap: 28, alignItems: "center", flexWrap: "wrap" }}>
        {["Privacy", "Terms", "Support", "Contact"].map((link) => (
          <a key={link} href="#" style={{ fontSize: 13, color: t.fg3, textDecoration: "none", letterSpacing: "-0.01em", transition: "color 0.2s" }}
            onMouseEnter={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = t.fg2)}
            onMouseLeave={(e) => ((e.currentTarget as HTMLAnchorElement).style.color = t.fg3)}>
            {link}
          </a>
        ))}
      </div>
      <p style={{ fontSize: 12, color: t.fg3, margin: 0 }}>© 2026 Rebalancer. All rights reserved.</p>
    </footer>
  )
}

// ─── Main ─────────────────────────────────────────────────────────────────────

export function LandingPage() {
  const [isDark, setIsDark] = useState(false)

  useEffect(() => {
    const mq = window.matchMedia("(prefers-color-scheme: dark)")
    setIsDark(mq.matches)
    const h = (e: MediaQueryListEvent) => setIsDark(e.matches)
    mq.addEventListener("change", h)
    return () => mq.removeEventListener("change", h)
  }, [])

  const t      = isDark ? DARK : LIGHT
  const toggle = useCallback(() => setIsDark((d) => !d), [])

  return (
    <div className="theme-wrap" style={{ backgroundColor: t.bg, color: t.fg, fontFamily: "'Plus Jakarta Sans', ui-sans-serif, system-ui, sans-serif", minHeight: "100vh", WebkitFontSmoothing: "antialiased", MozOsxFontSmoothing: "grayscale" }}>
      <Nav t={t} onToggle={toggle} />
      <main>
        <HeroSection t={t} />
        <StatementSection t={t} />
        <FeatureSlider t={t} />
        <OutcomesSection t={t} />
        <AgentSection t={t} />
        <LifeDimsSection t={t} />
        <PlatformsSection t={t} />
        <PhilosophySection t={t} />
        <DownloadSection t={t} />
      </main>
      <Footer t={t} />
    </div>
  )
}
